import React from 'react';
import { WizardStep } from './types';

interface WizardProgressProps {
  steps: WizardStep[];
  currentStep: number;
}

const WizardProgress: React.FC<WizardProgressProps> = ({ steps, currentStep }) => {
  const currentStepData = steps.find(step => step.id === currentStep);
  
  return (
    <div className="relative w-full">
      {/* Mobile Step Indicator */}
      <div className="md:hidden text-center mb-4">
        <div className="text-sm text-gray-400 mb-1">Step {currentStep} of {steps.length}</div>
        <h3 className="text-lg font-semibold text-white">
          <span className="mr-2">{currentStepData?.icon}</span>
          {currentStepData?.title}
        </h3>
      </div>
      
      {/* Desktop Progress Bar */}
      <div className="hidden md:flex items-center justify-center w-full">
        {steps.map((step, index) => {
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={step.id}>
              <div className="relative flex flex-col items-center">
                {/* Step Circle */}
                <div
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold
                    transition-all duration-300 border-2
                    ${isActive 
                      ? 'bg-blue-500 border-blue-500 text-white scale-110' 
                      : isCompleted 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : 'bg-gray-800 border-gray-600 text-gray-400'
                    }
                  `}
                >
                  {isCompleted ? (
                    <span className="text-xl">✓</span>
                  ) : (
                    <span className="text-xl">{step.icon}</span>
                  )}
                </div>
                
                {/* Step Title */}
                <div className="absolute -bottom-8 whitespace-nowrap">
                  <span 
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isActive 
                        ? 'text-blue-400' 
                        : isCompleted 
                          ? 'text-green-400' 
                          : 'text-gray-500'
                    }`}
                  >
                    {step.title}
                  </span>
                </div>
              </div>

              {/* Progress Line */}
              {!isLast && (
                <div className="flex-1 mx-4">
                  <div className="h-1 bg-gray-700 relative overflow-hidden rounded-full">
                    <div 
                      className={`
                        absolute top-0 left-0 h-full bg-blue-600
                        transition-all duration-500 ease-out
                        ${isCompleted ? 'w-full' : 'w-0'}
                      `}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Mobile Simple Progress Bar */}
      <div className="md:hidden flex items-center justify-center space-x-2">
        {steps.map((step) => (
          <div
            key={step.id}
            className={`
              w-3 h-3 rounded-full transition-all duration-300
              ${step.id === currentStep
                ? 'bg-blue-500 scale-125' 
                : step.id < currentStep
                  ? 'bg-green-500' 
                  : 'bg-gray-600'
              }
            `}
          />
        ))}
      </div>
    </div>
  );
};

export default WizardProgress;